import React, { useState } from "react";
import moment from "moment";
import GroupCheckBox from "./GroupCheckBox";
import CalendarDatePicker from "./CalendarDatePicker";
import TherapistSelect from "./TherapistSelect";
import FullCalendar from "@fullcalendar/react";
import { Box, IconButton, LinearProgress, Tooltip, Typography, ToggleButtonGroup, ToggleButton } from "@mui/material";
import BackplanModal from "./BackplanModal";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  FlagTriangleRight,
  RefreshCw,
  UserPlus,
} from "lucide-react";
import { Button as ShadButton } from "@/components/ui/button";
import { Switch as ShadSwitch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { UserDto } from "../../api-client";
import UserSelector from "./UserSelectorModal";
import useStore from "../../helpers/useStore";
import { UserContext } from "../../stores/User/User.provider";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";
import { observer } from "mobx-react";
import loadingStore from "../../stores/Loading/loadingStore";
import { CellSelectionData } from "./Calendar.type";
import { useDynamicTemplateProviderController } from "./DynamicTemplateProvider";
import { useEventShelfController } from "./EventShelf/EventShelfController";
import { mobiliTheme } from "../../themes/mobiliTheme";
import { useHorizon } from "./useHorizon";

interface HeaderToolbarProps {
  calendarRef: React.RefObject<FullCalendar>;
  onDatesSet: (date: Date) => void; // still needed to inform parent about date change side-effects
  onOutdated: () => void;
  users: UserDto[];
  onUserChange: (userId: string) => void;
  /** Ref to function that retrieves current cell selection from Calendar */
  getCellSelectionRef?: React.MutableRefObject<(() => CellSelectionData | null) | null>;
  /**
   * Populated by the toolbar with its date-navigation actions so the central
   * calendar hotkeys (j/k/J/K) drive EXACTLY the same logic as the toolbar
   * buttons (weekend skipping, focus-position capture, store/parent/FC sync).
   */
  keyboardNavigationRef?: React.MutableRefObject<CalendarDateNavigation | null>;
}

/** Date-navigation actions the toolbar exposes for the keyboard bindings. */
export type CalendarDateNavigation = {
  nextDay: () => void;
  previousDay: () => void;
  nextWeek: () => void;
  previousWeek: () => void;
};

const HeaderToolbar: React.FC<HeaderToolbarProps> = observer(
  ({ calendarRef, users, onOutdated, onDatesSet, onUserChange, getCellSelectionRef, keyboardNavigationRef }) => {
    const UserStore = useStore(UserContext);
    const { user } = UserStore;
    const location = user?.locationId;
    
    // Calendar store integration
    const CalendarStore = useStore(CalendarContext);
    const {
      selectedDateStr,
      setSelectedDateStr,
      isCancelledView,
      setCancelledView,
      groupCheck,
      setGroupCheck,
      continuousView,
      setContinuousView,
      timeSheetMode,
      setTimeSheetMode,
      singleTherapistView,
      setSingleTherapistView,
      singleTherapistUserId,
      setSingleTherapistUserId,
      setFocusPosition,
      setColumnUserIds,
      setIsRoleMode,
    } = CalendarStore;

    // Vorlagen expander state — shares the react-query caches with the
    // EventShelf's controller instances, so this costs no extra requests.
    const {
      handles: templateHandles,
      isOpen: templateWidgetOpen,
      canSeeFeaturePreview: canSeeTemplates,
      patientId: templatePatientId,
      toggleOpen: toggleTemplateWidget,
    } = useDynamicTemplateProviderController();
    const { shelves: bandShelves, isLoading: bandLoading } = useEventShelfController();
    // Scenario 1 ONLY: the whole planning band is hidden (no auto handles, not
    // summoned, no Offene Termine/Ablage). When the band is on screen its own
    // "Vorlagen ›" stub / section-header toggle takes over, so a control here
    // would be redundant.
    const showVorlagenExpand =
      canSeeTemplates &&
      !!templatePatientId &&
      !templateWidgetOpen &&
      templateHandles.length === 0 &&
      !bandLoading &&
      bandShelves.length === 0;
    
    // Global loading state
    const isGlobalLoading = loadingStore.isLoading;
    // Backend-configured planning horizon (jump target + out-of-bounds guard).
    const { horizonDateStr } = useHorizon();
    // Use centralized continuousView from store instead of local state
    const selectedDate = selectedDateStr; // store already holds string
    const [showHeaderTitle, setShowHeaderTitle] = useState<boolean>(true);
    const [openUserSelector, setOpenUserSelector] = useState<boolean>(false);
    const [selectedUserId, setSelectedUserId] = useState<string>("");  // Track selected user ID for dropdown
    const [backplanOpen, setBackplanOpen] = useState<boolean>(false);

    // Helper to capture cell selection before navigating
    const captureAndNavigate = (navigateFn: () => string) => {
      // Capture current cell selection before changing date and save to store
      if (getCellSelectionRef?.current) {
        const selection = getCellSelectionRef.current();
        if (selection) {
          setFocusPosition({ resourceId: selection.resourceId, time: selection.time });
        }
      }
      // Perform the navigation
      setDateFromString(navigateFn());
    };

    const setDateFromString = (date: string) => {
      setSelectedDateStr(date); // update store (string)
      onDatesSet(moment(date).toDate()); // downstream effects need Date
      calendarRef.current?.getApi().gotoDate(date); // FullCalendar
    };

    // const getSelectedDate = (): Date => {
    //   let calendarApi = calendarRef.current?.getApi()!;
    //   return calendarApi?.getDate();
    // };

    const handleViewModeChange = (
      event: React.MouseEvent<HTMLElement>,
      newMode: 'main' | 'continuous' | 'timesheet',
    ) => {
      if (newMode === null) return;
      
      // Update view mode flags
      setContinuousView(newMode === 'continuous');
      setTimeSheetMode(newMode === 'timesheet');
      
      // Clear user selection and filter
      setSelectedUserId("");
      onUserChange("");
      
      // Clear single therapist mode when switching views
      setSingleTherapistView(false);
      setSingleTherapistUserId(undefined);
      
      // Clear column filter when leaving continuous view
      if (newMode !== 'continuous') {
        setColumnUserIds([]);
        setIsRoleMode(true); // Restore default role mode so userIds are populated for appointment queries
        setShowHeaderTitle(true); // Show header title when not in continuous view
      }
    };
    
    const onCancelledToggleChange = (checked: boolean) => {
      setCancelledView(checked);
    };

    const computeNextDate = (days: number) => {
      const next = moment(selectedDate).add(days, "day");
      if (next.day() === 6) next.add(2, "day"); // skip Saturday
      if (next.day() === 0) next.add(1, "day"); // skip Sunday
      return next.format("YYYY-MM-DD");
    };

    const computePreviousDate = (days: number) => {
      const prev = moment(selectedDate).subtract(days, "day");
      if (prev.day() === 0) prev.subtract(2, "day"); // skip Sunday
      if (prev.day() === 6) prev.subtract(1, "day"); // skip Saturday
      return prev.format("YYYY-MM-DD");
    };

    const onNextDate = () => {
      captureAndNavigate(() => singleTherapistView ? computeNextDate(14) : computeNextDate(1));
    };

    const onPreviousDate = () => {
      captureAndNavigate(() => singleTherapistView ? computePreviousDate(14) : computePreviousDate(1));
    };

    const onNextWeek = () => {
      captureAndNavigate(() => computeNextDate(7));
    };

    const onPreviousWeek = () => {
      captureAndNavigate(() => computePreviousDate(7));
    };

    // Trampoline for the keyboard bindings: reassigned every render so the
    // closures always see the current selectedDate / view flags.
    if (keyboardNavigationRef) {
      keyboardNavigationRef.current = {
        nextDay: onNextDate,
        previousDay: onPreviousDate,
        nextWeek: onNextWeek,
        previousWeek: onPreviousWeek,
      };
    }

    const onGoToday = () => {
      captureAndNavigate(() => moment().format("YYYY-MM-DD"));
    };

    const onDatePickerChange = (date: string) => {
      // console.log("Header is updating date from string:", date);
      captureAndNavigate(() => date);
    };

    const onUserSelected = (userId: string) => {
      setSelectedUserId(userId || "");  // Track selected user ID locally
      userId ? setShowHeaderTitle(false) : setShowHeaderTitle(true);
      onUserChange(userId);
    };

    const onMainViewTherapistSelected = (userId: string) => {
      setSelectedUserId(userId || "");
      setSingleTherapistUserId(userId || undefined);
      setSingleTherapistView(!!userId);
      userId ? setShowHeaderTitle(false) : setShowHeaderTitle(true);
    };

    const handleUserSelectoreModal = () => {
      setOpenUserSelector(true);
    };

    return (
      // mb: breathing room between the (now compact) toolbar and the calendar
      // grid below; the loading bar renders inside this gap when active.
      <Box display="flex" flexDirection="column" width="100%" mb={1}>
        {/* One toolbar row, four zones with consistent 32px control heights:
            [Vorlagen] | date navigation | current date | view mode (centered) | filters & people */}
        <Box
          display="flex"
          alignItems={"center"}
          gap={1}
          rowGap={0.5}
          // One row, always. When it runs short the "Stornierungen" LABEL
          // clips (see zone 4) rather than the toolbar breaking onto a second
          // line — the band below is worth more than the word.
          flexWrap="nowrap"
          width="100%"
        >
          {/* Vorlagen expander — a deliberately small, subtle arrowhead, first
              in the toolbar so it sits at the far left, directly under where
              the planning band unfolds. Lives here rather than in the band so
              an empty band can disappear entirely without reserving a
              full-width row for one control. */}
          {showVorlagenExpand && (
            <Tooltip title="Vorlagen einblenden" arrow>
              <IconButton
                size="small"
                onClick={toggleTemplateWidget}
                sx={{ opacity: 0.55, "&:hover": { opacity: 1 } }}
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {/* ── Zone 1: date navigation ───────────────────────────── */}
          <Box display="flex" alignItems="center" gap={0.5}>
            <Tooltip title="Woche zurück" arrow>
              <ShadButton variant="outline" className="h-8 w-8 p-0" onClick={onPreviousWeek}>
                <ChevronsLeft />
              </ShadButton>
            </Tooltip>
            <Tooltip title="Tag zurück" arrow>
              <ShadButton variant="outline" className="h-8 w-8 p-0" onClick={onPreviousDate}>
                <ChevronLeft />
              </ShadButton>
            </Tooltip>
            <ShadButton
              variant={selectedDate === moment().format("YYYY-MM-DD") ? "default" : "outline"}
              className="h-8 px-2.5"
              onClick={onGoToday}
            >
              Heute
            </ShadButton>
            <Tooltip title="Tag vor" arrow>
              <ShadButton variant="outline" className="h-8 w-8 p-0" onClick={onNextDate}>
                <ChevronRight />
              </ShadButton>
            </Tooltip>
            <Tooltip title="Woche vor" arrow>
              <ShadButton variant="outline" className="h-8 w-8 p-0" onClick={onNextWeek}>
                <ChevronsRight />
              </ShadButton>
            </Tooltip>
            {!continuousView && !timeSheetMode && horizonDateStr && (
              <Tooltip title="Zum Planungshorizont springen" arrow>
                <ShadButton
                  variant="outline"
                  className="h-8 w-8 p-0"
                  onClick={() => setSelectedDateStr(horizonDateStr)}
                >
                  <FlagTriangleRight />
                </ShadButton>
              </Tooltip>
            )}
            {!continuousView && !timeSheetMode && (
              <CalendarDatePicker date={selectedDate} onChange={onDatePickerChange} />
            )}
            <Tooltip title="Aktualisieren" arrow>
              <ShadButton variant="outline" className="h-8 w-8 p-0" onClick={onOutdated}>
                <RefreshCw />
              </ShadButton>
            </Tooltip>
          </Box>

          <Separator orientation="vertical" className="h-6 self-center" />

          {/* ── Zone 2: current date ──────────────────────────────── */}
          {showHeaderTitle && (
            <Typography
              noWrap
              component="h2"
              sx={{ fontWeight: "bold", fontSize: "1.05rem", minWidth: 190 }}
            >
              {moment(selectedDate).format(continuousView ? "dddd" : "dddd, D. MMM YYYY")}
            </Typography>
          )}

          {/* ── Zone 3: view mode (centered in remaining space) ───────── */}
          {/* Grows into spare room and centres the group, but never shrinks past
              it. This was `flex: 1, minWidth: 0`: flex-basis 0 meant the group's
              real width counted for nothing when the row worked out whether it
              fit, and minWidth 0 removed the content floor every flex item has
              by default — so this box collapsed BELOW the group, the group
              overflowed, and centring spilled it out BOTH sides, which is how
              "Arbeitszeiten" ended up under the Stornierungen switch. */}
          <Box
            sx={{
              flexGrow: 1,
              flexShrink: 0,
              flexBasis: "auto",
              display: "flex",
              justifyContent: "center",
            }}
          >
          <ToggleButtonGroup
            value={continuousView ? 'continuous' : timeSheetMode ? 'timesheet' : 'main'}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            // Never let the three labels compress into "A…EITS…".
            sx={{ height: 32, flexShrink: 0 }}
          >
            <ToggleButton 
              value="main" 
              sx={{ 
                px: 1.5, 
                py: 0.5, 
                fontSize: '0.875rem',
                '&.Mui-selected': {
                  backgroundColor: mobiliTheme.palette.secondary.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: mobiliTheme.palette.secondary.main,
                  }
                }
              }}
            >
              Hauptkalender
            </ToggleButton>
            <ToggleButton 
              value="continuous" 
              sx={{ 
                px: 1.5, 
                py: 0.5, 
                fontSize: '0.875rem',
                '&.Mui-selected': {
                  backgroundColor: mobiliTheme.palette.secondary.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: mobiliTheme.palette.secondary.main,
                  }
                }
              }}
            >
              Dauertermin
            </ToggleButton>
            <ToggleButton 
              value="timesheet" 
              sx={{ 
                px: 1.5, 
                py: 0.5, 
                fontSize: '0.875rem',
                '&.Mui-selected': {
                  backgroundColor: mobiliTheme.palette.secondary.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: mobiliTheme.palette.secondary.main,
                  }
                }
              }}
            >
              Arbeitszeiten
            </ToggleButton>
          </ToggleButtonGroup>
          </Box>

          {/* ── Zone 4: filters & people ──────────────────────────── */}
          <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0 }}>
            {/* View filters: what the calendar shows */}
            <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
              <label className="flex cursor-pointer items-center gap-1.5 min-w-0">
                {/* The switch itself never shrinks — a clipped hit target is
                    unusable, and it stays operable with no label at all. */}
                <span className="shrink-0">
                  <ShadSwitch
                    checked={isCancelledView}
                    onCheckedChange={onCancelledToggleChange}
                  />
                </span>
                {/* The one thing in the toolbar allowed to give. */}
                <span className="text-xs font-medium truncate min-w-0">Stornierungen</span>
              </label>
              {!continuousView && !timeSheetMode && <GroupCheckBox />}
            </Box>
            {/* People: whose columns are on the board */}
            <Box display="flex" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
              {!timeSheetMode && (
                <TherapistSelect
                  users={users}
                  value={
                    (continuousView ? selectedUserId : singleTherapistUserId || selectedUserId) || ""
                  }
                  onChange={(userId) =>
                    continuousView ? onUserSelected(userId) : onMainViewTherapistSelected(userId)
                  }
                />
              )}
              <Tooltip title="Benutzer auswählen" arrow>
                <ShadButton
                  variant="outline"
                  className="h-8 w-8 p-0"
                  onClick={handleUserSelectoreModal}
                >
                  <UserPlus />
                </ShadButton>
              </Tooltip>
            </Box>
            {continuousView && (
              <>
                <ShadButton
                  variant="outline"
                  className="h-8 px-2.5"
                  onClick={() => setBackplanOpen(true)}
                >
                  Backplan
                </ShadButton>
                <BackplanModal open={backplanOpen} onClose={() => setBackplanOpen(false)} />
              </>
            )}
            {openUserSelector && (
              <UserSelector
                open={openUserSelector}
                locationId={location!}
                onConfirm={onOutdated}
                onClose={() => setOpenUserSelector(false)}
              />
            )}
          </Box>
        </Box>
        <Box
          display="flex"
          width="100%"
          border={"0px solid purple"}
          textAlign={"center"}
          align-items={"center"}
        >
          {isGlobalLoading && (
            <LinearProgress sx={{ width: "100%" }} color="info" />
          )}
        </Box>
      </Box>
    );
  }
);
HeaderToolbar.displayName = "HeaderToolbar";
export default HeaderToolbar;
